import { useState, useCallback } from 'react';
import { useFCBaseline } from '../../fcOptimizer/hooks/useFCBaseline';
import { calculateFromMargin, calculateTotalCost } from '../utils/pricingCalculations';

/**
 * Hook for batch price calculation with FC Optimizer integration.
 *
 * Calculates prices for multiple products using a pricing template,
 * prioritizing FC Optimizer fulfillment cost data when available.
 *
 * @returns {object} Hook interface
 *   - calculateBatchPrices(products, template): Async function to calculate prices
 *   - isCalculating: boolean loading state
 *   - fcBaselineLoaded: boolean indicating if FC baseline data is loaded
 */
export function usePricingCalculation() {
  const [isCalculating, setIsCalculating] = useState(false);

  // Get FC Optimizer baseline data
  const { baseline, baselineLoading, loadBaseline, getBaselineFC } = useFCBaseline();

  // Determine if baseline is loaded (has data or finished loading)
  const fcBaselineLoaded = !baselineLoading && baseline.size >= 0;

  /**
   * Calculate prices for a batch of products using a pricing template.
   *
   * For each product:
   * 1. Gets fulfillment cost from FC Optimizer data if available
   * 2. Falls back to template's additionalFulfillmentCost or $6.50 default
   * 3. Calculates total cost and applies margin formula
   *
   * @param {Array} products - Array of product objects with { sku, cost, ref, ... }
   * @param {object} template - Pricing template with { id, name, settings: { prefMargin, additionalFulfillmentCost, ... } }
   * @returns {object} Results with { results, errors, summary }
   */
  const calculateBatchPrices = useCallback(async (products, template) => {
    if (!products || products.length === 0) {
      return {
        results: [],
        errors: [],
        summary: { total: 0, calculated: 0, skipped: 0 }
      };
    }

    setIsCalculating(true);

    try {
      // Ensure baseline is loaded
      if (!fcBaselineLoaded && !baselineLoading) {
        await loadBaseline();
      }

      const results = [];
      const errors = [];
      const DEFAULT_FULFILLMENT_COST = 6.50;

      // Get margin from template settings (stored as decimal, e.g., 0.30 for 30%)
      const marginPercent = (template?.settings?.prefMargin || 0.30) * 100;

      for (const product of products) {
        const sku = product.sku;
        const baseCost = Number(product.cost);
        const productFactor = Number(product.productFactor) || 1;
        // Apply product factor to get actual cost (e.g., $2.45 × 12 = $29.40)
        const cost = baseCost * productFactor;

        // Skip products without valid cost
        if (baseCost === null || baseCost === undefined || isNaN(baseCost) || baseCost <= 0) {
          errors.push({
            sku,
            error: 'Missing or invalid cost',
            product
          });
          continue;
        }

        // Get fulfillment cost: FC Optimizer > template fallback > default
        let fulfillmentCost;
        let fulfillmentSource;

        const fcOptimizerCost = getBaselineFC(sku);
        if (fcOptimizerCost !== null && fcOptimizerCost !== undefined) {
          fulfillmentCost = fcOptimizerCost;
          fulfillmentSource = 'fcOptimizer';
        } else {
          fulfillmentCost = Number(template?.settings?.additionalFulfillmentCost) || DEFAULT_FULFILLMENT_COST;
          fulfillmentSource = 'fallback';
        }

        // Calculate total cost using utility function
        const costBreakdown = calculateTotalCost(product, template, baseline);

        // Calculate price using margin formula
        try {
          const priceResult = calculateFromMargin(costBreakdown.totalCost, marginPercent);

          results.push({
            sku,
            breakdown: {
              cogs: cost,
              fulfillmentCost: costBreakdown.fulfillmentCost,
              fulfillmentSource: costBreakdown.fulfillmentSource,
              additionalFees: costBreakdown.additionalFees,
              totalCost: costBreakdown.totalCost,
              price: priceResult.price,
              marginPercent: priceResult.marginPercent,
              marginAmount: priceResult.marginAmount,
              markupPercent: priceResult.markupPercent
            }
          });
        } catch (calcError) {
          errors.push({
            sku,
            error: calcError.message,
            product
          });
        }
      }

      return {
        results,
        errors,
        summary: {
          total: products.length,
          calculated: results.length,
          skipped: errors.length
        }
      };
    } finally {
      setIsCalculating(false);
    }
  }, [baseline, baselineLoading, fcBaselineLoaded, loadBaseline, getBaselineFC]);

  return {
    calculateBatchPrices,
    isCalculating,
    fcBaselineLoaded
  };
}

export default usePricingCalculation;
